
'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useState } from 'react';

export function LuxuryMinimalDarkHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-black text-neutral-300 font-sans tracking-tight">
      <header className="relative top-0 left-0 right-0 p-6 md:p-8 z-20 flex justify-between items-center">
        <h2 className="text-xl font-bold text-white tracking-widest uppercase">
          NOIR
        </h2>
        <nav className="hidden md:flex gap-8 text-xs uppercase tracking-wider text-neutral-400">
          <a href="#" className="hover:text-white transition-colors">
            Residences
          </a>
          <a href="#" className="hover:text-white transition-colors">
            Amenities
          </a>
          <a href="#" className="hover:text-white transition-colors">
            Inquire
          </a>
        </nav>
        <div className="md:hidden">
            <Button size="icon" variant="ghost" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <X className="text-white" /> : <Menu className="text-white" />}
            </Button>
        </div>
      </header>

       {isMenuOpen && (
        <div className="fixed inset-0 bg-black/95 z-30 flex items-center justify-center md:hidden">
          <button
            className="absolute top-6 right-6 text-white"
            onClick={() => setIsMenuOpen(false)}
          >
            <X size={32} />
          </button>
          <nav className="flex flex-col gap-8 text-2xl text-center text-neutral-400 uppercase tracking-wider">
            <a href="#" className="hover:text-white transition-colors" onClick={() => setIsMenuOpen(false)}>Residences</a>
            <a href="#" className="hover:text-white transition-colors" onClick={() => setIsMenuOpen(false)}>Amenities</a>
            <a href="#" className="hover:text-white transition-colors" onClick={() => setIsMenuOpen(false)}>Inquire</a>
          </nav>
        </div>
      )}


      <main>
        <section className="h-screen flex items-end pb-24 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent z-10"></div>
          <Image
            src="https://picsum.photos/seed/luxdark1/1800/1200"
            alt="A dimly lit, modern apartment interior with city lights in the background"
            fill
            className="object-cover"
            priority
            data-ai-hint="modern apartment"
          />
          <div className="container mx-auto px-8 relative z-20">
            <h1 className="text-5xl md:text-8xl font-medium text-white leading-none">
              Life, Elevated.
            </h1>
            <p className="mt-4 max-w-xl text-neutral-400">
              A collection of private residences designed for the discerning
              few. Unparalleled views, uncompromising quality.
            </p>
            <Button
              variant="link"
              className="mt-6 p-0 text-white group text-lg"
            >
              Discover the Penthouse{' '}
              <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </section>

        <section className="py-24 md:py-32 bg-[#0A0A0A] px-4">
          <div className="container mx-auto grid md:grid-cols-2 gap-16 items-center">
            <div>
              <Image
                src="https://picsum.photos/seed/luxdark2/1000/1200"
                alt="A close-up of a marble countertop with minimalist black fixtures"
                width={1000}
                height={1200}
                className="w-full h-auto"
                data-ai-hint="marble countertop"
              />
            </div>
            <div className="max-w-md">
              <h2 className="text-3xl md:text-4xl font-medium text-white">
                The Art of Detail.
              </h2>
              <p className="mt-4 text-neutral-400">
                Every element is a testament to meticulous craftsmanship. From
                the Italian marble to the custom-milled oak flooring, NOIR is
                an exercise in restraint and refinement.
              </p>
            </div>
          </div>
        </section>

        <section className="py-24 md:py-32 px-4">
          <div className="container mx-auto">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-medium text-white">
                Sanctuary in the Sky.
              </h2>
              <p className="mt-4 text-neutral-400">
                Our state-of-the-art amenities provide a private escape from
                the city below.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 mt-16">
              <div className="border border-neutral-800 p-8">
                <h3 className="text-xl font-medium text-white">
                  Rooftop Pool
                </h3>
                <p className="mt-2 text-neutral-500">
                  Heated, saline infinity pool with panoramic city views.
                </p>
              </div>
              <div className="border border-neutral-800 p-8">
                <h3 className="text-xl font-medium text-white">
                  Private Wellness
                </h3>
                <p className="mt-2 text-neutral-500">
                  Fully-equipped gym, sauna, and dedicated yoga studio.
                </p>
              </div>
              <div className="border border-neutral-800 p-8">
                <h3 className="text-xl font-medium text-white">Concierge</h3>
                <p className="mt-2 text-neutral-500">
                  24/7 resident services for a life of effortless comfort.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 md:py-32 bg-[#050505] px-4">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-medium text-white">
              Schedule a Private Viewing.
            </h2>
            <p className="mt-4 text-neutral-400 max-w-xl mx-auto">
              Experience the extraordinary. Contact our sales gallery to
              arrange your confidential tour.
            </p>
            <Button
              variant="outline"
              className="mt-10 rounded-none uppercase tracking-widest px-10 py-7 border-neutral-700 text-neutral-300 hover:border-white hover:bg-transparent hover:text-white"
            >
              Inquire Now
            </Button>
          </div>
        </section>
      </main>
      <footer className="border-t border-neutral-900 py-8 px-4">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center text-xs text-neutral-600 gap-4">
          <p>
            &copy; {new Date().getFullYear()} NOIR Residences. All rights reserved.
          </p>
          <nav className="flex gap-6 uppercase tracking-wider">
            <a href="#" className="hover:text-white">
              Legal
            </a>
            <a href="#" className="hover:text-white">
              Media
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
